import React, { useMemo } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";

import AgGrid from "components/AgGrid/AgGrid";

import { Column } from "constants/columns";
import { NLISFileTypes } from "constants/nlis";

import { withNestedField } from "lib/agGrid";
import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";

import {
  type ScanListRow,
  type ScanTransferRowBase,
} from "selectors/aggrid/scans";

interface NlisCorrectionsRow extends ScanTransferRowBase {
  latestSellTransfer: ScanListRow["latestSellTransfer"];
  latestTakeTransfer: ScanListRow["latestTakeTransfer"];
  saleLot: ScanListRow["saleLot"] & {
    destinationProperty?: { PIC: string };
  };
}

const defaultColDef: ColDef = {
  sortable: true,
  flex: 1,
  resizable: true,
  enableCellChangeFlash: true,
};

const getColumnDefs = (
  nlisFileType: string,
): (ColDef<NlisCorrectionsRow> | ColGroupDef<NlisCorrectionsRow>)[] => {
  return (
    [
      {
        headerName: "EID",
        field: "scan.EID",
        minWidth: 90,
      },
      {
        headerName: "AgriNous",
        marryChildren: true,
        children: [
          BaseColumnDefinitions[Column.NVD],
          withNestedField(Column.CURRENT_PIC, "scan"),
          BaseColumnDefinitions[Column.VENDOR_PIC],
          withNestedField(Column.DESTINATION_PIC, "saleLot"),
        ],
      },
      nlisFileType === NLISFileTypes.NLISTakeFile
        ? {
            headerName: "NLIS Take",
            marryChildren: true,
            children: [
              BaseColumnDefinitions[Column.NLIS_TAKE_TRANSFER_VENDOR_PIC],
              BaseColumnDefinitions[Column.NLIS_TAKE_TRANSFER_NVD],
            ],
          }
        : null,
      nlisFileType === NLISFileTypes.NLISSellFile
        ? {
            headerName: "NLIS Sell",
            marryChildren: true,
            children: [
              BaseColumnDefinitions[Column.NLIS_SELL_TRANSFER_BUYER_PIC],
            ],
          }
        : null,
    ] as (ColDef<NlisCorrectionsRow> | ColGroupDef<NlisCorrectionsRow> | null)[]
  ).filter(Boolean) as (
    | ColDef<NlisCorrectionsRow>
    | ColGroupDef<NlisCorrectionsRow>
  )[];
};

interface NLISCorrectionGridProps {
  nlisFileType: string;
  rowData: NlisCorrectionsRow[];
}

export const NLISCorrectionGrid = ({
  rowData,
  nlisFileType,
}: NLISCorrectionGridProps) => {
  const columnDefs = useMemo(() => getColumnDefs(nlisFileType), [nlisFileType]);

  return (
    <AgGrid
      rowData={rowData}
      columnDefs={columnDefs}
      pagination
      paginationPageSize={10}
      defaultColDef={defaultColDef}
      enableCellTextSelection
    />
  );
};
